import React, { useState } from 'react';

const ProductCategories: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('identity');

  const categories = [
    {
      id: 'identity',
      title: 'Identity and Access Management',
      description: 'Manage, govern, and secure digital identities and privileged access.',
      icon: 'ri-shield-user-line',
      products: [
        {
          name: 'ADManager Plus',
          description: 'Active Directory, Microsoft 365, and Exchange management and reporting',
          features: ['Active Directory user management', 'Microsoft 365 management', 'Built-in report library', 'Multi-approval workflow'],
          image: 'https://public.readdy.ai/ai/img_res/ba310e813007afbeb40a1307d8cd93c3.jpg'
        },
        {
          name: 'ADAudit Plus',
          description: 'Hybrid AD, cloud, and file auditing; security; and compliance',
          features: ['Real-time change monitoring', 'User logon monitoring', 'Anomaly detection using UBA', 'Compliance reports'],
          image: 'https://readdy.ai/api/search-image?query=Security%20audit%20dashboard%20showing%20real-time%20monitoring%20alerts%2C%20compliance%20reports%2C%20and%20user%20activity%20logs%20on%20professional%20computer%20interface&width=400&height=300&seq=adaudit-1&orientation=landscape'
        },
        {
          name: 'PAM360',
          description: 'Complete privileged access security for enterprises',
          features: ['Enterprise credential vault', 'Just-in-time privilege elevation', 'Session recording', 'SSH key management'],
          image: 'https://readdy.ai/api/search-image?query=Privileged%20access%20management%20vault%20interface%20with%20secure%20credentials%2C%20access%20controls%2C%20and%20session%20monitoring%20on%20professional%20security%20dashboard&width=400&height=300&seq=pam360-1&orientation=landscape'
        }
      ]
    },
    {
      id: 'service',
      title: 'Unified Service Management',
      description: 'Design, automate, deliver, and manage IT and business services.',
      icon: 'ri-customer-service-2-line',
      products: [
        {
          name: 'ServiceDesk Plus',
          description: 'AI-driven unified service management platform',
          features: ['Best practice ITSM capabilities', 'Intelligent automations', 'Enterprise service desk', 'Native IT integrations'],
          image: 'https://readdy.ai/api/search-image?query=IT%20service%20desk%20dashboard%20with%20ticket%20management%2C%20workflow%20automation%2C%20and%20service%20catalog%20on%20modern%20professional%20interface&width=400&height=300&seq=servicedesk-1&orientation=landscape'
        },
        {
          name: 'AssetExplorer',
          description: 'IT asset management with an integrated CMDB',
          features: ['IT asset inventory management', 'Software asset management', 'Purchase order management', 'Asset lifecycle management'],
          image: 'https://readdy.ai/api/search-image?query=IT%20asset%20management%20dashboard%20showing%20hardware%20inventory%2C%20software%20licenses%2C%20and%20asset%20tracking%20on%20professional%20computer%20interface&width=400&height=300&seq=assetexplorer-1&orientation=landscape'
        }
      ]
    },
    {
      id: 'endpoint',
      title: 'Unified Endpoint Management',
      description: 'Workstations, laptops, mobile devices, servers, browsers',
      icon: 'ri-computer-line',
      products: [
        {
          name: 'Endpoint Central',
          description: 'Integrated endpoint management and protection platform',
          features: ['Patch management', 'Software deployment', 'Mobile device management', 'Remote control'],
          image: 'https://readdy.ai/api/search-image?query=Endpoint%20management%20dashboard%20showing%20device%20inventory%2C%20patch%20status%2C%20and%20mobile%20device%20management%20on%20professional%20IT%20interface&width=400&height=300&seq=endpoint-1&orientation=landscape'
        },
        {
          name: 'Patch Manager Plus',
          description: 'Automated patching across multiple OSs and 850+ third-party apps',
          features: ['Automated patch management', 'Third-party application patching', 'Cross-platform support', 'Compliance checks'],
          image: 'https://readdy.ai/api/search-image?query=Patch%20management%20interface%20showing%20software%20updates%2C%20security%20patches%2C%20and%20deployment%20status%20across%20multiple%20operating%20systems&width=400&height=300&seq=patchmanager-1&orientation=landscape'
        }
      ]
    },
    {
      id: 'monitoring',
      title: 'IT Operations Management',
      description: 'Monitor and manage your network, servers, and applications.',
      icon: 'ri-line-chart-line',
      products: [
        {
          name: 'OpManager Plus',
          description: 'Full-stack observability with AIOps and digital experience monitoring',
          features: ['IT operations management', 'Network management', 'Data center infrastructure', 'Unified monitoring'],
          image: 'https://readdy.ai/api/search-image?query=Network%20monitoring%20dashboard%20with%20real-time%20performance%20metrics%2C%20server%20status%2C%20and%20infrastructure%20topology%20on%20professional%20IT%20interface&width=400&height=300&seq=opmanager-1&orientation=landscape'
        },
        {
          name: 'Site24x7',
          description: 'AI-powered, all-in-one SaaS platform for observability',
          features: ['Website performance monitoring', 'Application monitoring', 'Real user monitoring', 'Cloud monitoring'],
          image: 'https://readdy.ai/api/search-image?query=Website%20performance%20monitoring%20dashboard%20showing%20uptime%20statistics%2C%20response%20times%2C%20and%20global%20monitoring%20locations%20on%20cloud%20interface&width=400&height=300&seq=site24x7-1&orientation=landscape'
        }
      ]
    }
  ];

  const activeData = categories.find(cat => cat.id === activeCategory);

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Comprehensive IT Management Solutions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our complete suite of IT management and security products designed to streamline your operations
          </p>
        </div>

        {/* Category Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center space-x-3 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
              }`}
            >
              <div className={`w-6 h-6 flex items-center justify-center`}>
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <span>{category.title}</span>
            </button>
          ))}
        </div>

        {/* Active Category Content */}
        {activeData && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className={`${activeData.icon} text-3xl text-blue-600`}></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">{activeData.title}</h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">{activeData.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {activeData.products.map((product, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="mb-6">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{product.name}</h4>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <i className="ri-check-line text-green-500 text-sm mt-1 flex-shrink-0"></i>
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                    Learn More
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductCategories;